/**
 * Конкурсы: список и экран конкурса.
 *
 * Конкурс — не акция. У акции победителя считает формула от публичного сида, и
 * честность доказывается пересчётом. Здесь победителя выбирают люди, и доказать
 * «правильность вкусов» нельзя — можно только показать процедуру целиком: какие
 * этапы, кто голосует, сколько у тебя голосов и чем всё кончилось. Поэтому на
 * экране всегда видно, на каком этапе конкурс и что именно сейчас разрешено.
 *
 * Счётчики голосов по умолчанию скрыты до итогов: видимый счётчик тянет голоса
 * к лидеру, и выигрывает тот, кто вырвался первым, а не тот, чья работа лучше.
 */
import type { Contest, ContestEntry, ContestView } from '@allswipes/client';
import { Badge, BrandMark, Button, Card, Countdown, Empty, Field, Icon, Skeleton, VerifiedMark, fmtDate, num } from '@allswipes/ui';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../api';
import { useReveal } from '../anim';
import { Shell } from '../components/Shell';
import { useResource } from '../hooks';
import { useSession } from '../session';
import { useToast } from '../toast';

import { usePlural, useT } from '@allswipes/i18n';

const KIND_NAMES: Record<string, string> = {
  photo: 'Фото',
  illustration: 'Иллюстрация',
  video: 'Видео',
  text: 'Текст',
  project: 'Проект',
};

type Tone = 'live' | 'good' | 'warn' | 'prize' | undefined;

const STAGE: Record<string, { label: string; tone: Tone }> = {
  open: { label: 'приём работ', tone: 'live' },
  voting: { label: 'голосование', tone: 'warn' },
  finished: { label: 'итоги', tone: 'good' },
};

function stageOf(c: Contest): { label: string; tone: Tone } {
  return STAGE[c.status] ?? { label: c.status, tone: undefined };
}

/** Срок, к которому идёт текущий этап. */
function deadline(c: Contest) {
  if (c.status === 'open') return c.submit_to;
  if (c.status === 'voting') return c.vote_to;
  return undefined;
}

export function ContestsScreen() {
  const t = useT();
  const { profile } = useSession();
  const list = useResource(
    () => api.contests({ region: profile?.user.region }),
    [profile?.user.region],
  );
  const root = useRef<HTMLDivElement>(null);
  useReveal(root, [list.data], { selector: '.ccard' });

  return (
    <Shell
      wide
      title={t('Конкурсы')}
      subtitle={t('здесь выигрывает работа, а не билет: присылай свою и голосуй за чужие')}
    >
      {list.loading && <Skeleton count={3} height={120} />}
      {list.data && list.data.contests.length === 0 && (
        <Empty icon="star" title={t('Конкурсов сейчас нет')}>
          {t('Когда платформа объявит конкурс, он появится здесь — с условиями, сроками и работами участников.')}
        </Empty>
      )}
      <div ref={root} className="ccards">
        {list.data?.contests.map((c) => (
          <ContestCard key={c.id} contest={c} />
        ))}
      </div>
    </Shell>
  );
}

function ContestCard({ contest: c }: { contest: Contest }) {
  const t = useT();
  const plural = usePlural();
  const stage = stageOf(c);
  const to = deadline(c);
  return (
    <Link to={`/k/${c.slug || c.id}`} className="ccard" style={{ textDecoration: 'none' }}>
      <Card>
        <div className="row" style={{ gap: 8, marginBottom: 8 }}>
          <Badge tone={stage.tone} dot={c.status === 'open'}>
            {t(stage.label)}
          </Badge>
          <Badge>{t(KIND_NAMES[c.kind] ?? c.kind)}</Badge>
          <span className="spacer" />
          {to && <Countdown to={to} />}
        </div>
        <h2 className="h2" style={{ fontSize: 17, marginBottom: 4 }}>
          {c.title}
        </h2>
        {c.tagline && (
          <p className="sub" style={{ marginBottom: 10 }}>
            {c.tagline}
          </p>
        )}
        <div className="row faint" style={{ gap: 14, fontSize: 12 }}>
          <span>
            {num(c.entries)} {plural(c.entries, 'работа', 'работы', 'работ')}
          </span>
          {c.prize && <span>{c.prize}</span>}
          <span>
            {c.winners} {plural(c.winners, 'место', 'места', 'мест')}
          </span>
        </div>
      </Card>
    </Link>
  );
}

export function ContestScreen() {
  const t = useT();
  const { id = '' } = useParams();
  const view = useResource(() => api.contest(id), [id]);
  const data = view.data;

  if (view.loading) {
    return (
      <Shell wide>
        <Skeleton count={4} height={110} />
      </Shell>
    );
  }
  if (view.error || !data) {
    return (
      <Shell wide title={t('Конкурс')}>
        <Empty icon="search" title={t('Конкурс не найден')}>
          {view.error ?? t('Возможно, ссылка устарела.')}
        </Empty>
      </Shell>
    );
  }

  return <ContestBody view={data} reload={view.reload} />;
}

function ContestBody({ view, reload }: { view: ContestView; reload: () => void }) {
  const plural = usePlural();
  const t = useT();
  const { contest: c, brand, entries, mine } = view;
  const stage = stageOf(c);
  const to = deadline(c);
  const grid = useRef<HTMLDivElement>(null);
  useReveal(grid, [entries.length, view.votes_left], { selector: '.kentry' });

  return (
    <Shell wide>
      <Card style={{ marginBottom: 18 }}>
        <div className="row" style={{ gap: 8, marginBottom: 10 }}>
          <Badge tone={stage.tone} dot={c.status === 'open'}>
            {t(stage.label)}
          </Badge>
          <Badge>{t(KIND_NAMES[c.kind] ?? c.kind)}</Badge>
          <span className="spacer" />
          {to && <Countdown to={to} />}
        </div>

        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          {brand && <BrandMark name={brand.name} logoUrl={brand.logo_url} size={40} />}
          <div style={{ minWidth: 0 }}>
            <h1 className="h1" style={{ fontSize: 22 }}>
              {c.title}
            </h1>
            {brand && (
              <div className="row faint" style={{ gap: 6, fontSize: 12, marginTop: 2 }}>
                <Link to={`/b/${brand.slug}`}>{brand.name}</Link>
                {brand.verified_at && <VerifiedMark size={12} />}
              </div>
            )}
          </div>
        </div>

        {c.tagline && (
          <p className="sub" style={{ marginTop: 10 }}>
            {c.tagline}
          </p>
        )}
        {c.about && (
          <p className="sub" style={{ marginTop: 8, whiteSpace: 'pre-line' }}>
            {c.about}
          </p>
        )}

        <Conditions view={view} />
      </Card>

      {c.status === 'open' && <SubmitBlock view={view} reload={reload} />}

      {view.winners && view.winners.length > 0 && (
        <>
          <div className="sec-head">
            <span className="lab">{t('Победители')}</span>
            <span className="count">{t('итоги подведены {day}', { day: fmtDate(c.updated_at) })}</span>
          </div>
          <div className="kentries" style={{ marginBottom: 20 }}>
            {view.winners.map((e, i) => (
              <EntryCard key={e.id} entry={e} view={view} reload={reload} place={i + 1} />
            ))}
          </div>
        </>
      )}

      <div className="sec-head">
        <span className="lab">{c.status === 'finished' ? t('Все работы') : t('Работы участников')}</span>
        <span className="count">
          {num(entries.length)} {plural(entries.length, 'работа', 'работы', 'работ')}
        </span>
      </div>

      {entries.length === 0 ? (
        <Empty icon="star" title={t('Работ пока нет')}>
          {c.status === 'open'
            ? t('Пришли свою — она появится здесь после проверки.')
            : t('Ни одна работа не прошла проверку.')}
        </Empty>
      ) : (
        <div ref={grid} className="kentries">
          {entries.map((e) => (
            <EntryCard key={e.id} entry={e} view={view} reload={reload} />
          ))}
        </div>
      )}

      {mine && (
        <Card style={{ marginTop: 18 }}>
          <div className="row" style={{ gap: 8 }}>
            <Icon name="check" size={14} />
            <span className="sub">
              {t('Твоя работа: {title} — {state}', {
                title: mine.title || t('без названия'),
                state:
                  mine.status === 'approved'
                    ? t('опубликована')
                    : mine.status === 'pending'
                      ? t('на проверке')
                      : `${t('не принята')}${mine.reject_note ? `: ${mine.reject_note}` : ''}`,
              })}
            </span>
          </div>
        </Card>
      )}
    </Shell>
  );
}

/** Условия конкурса одним блоком: их объявляют до начала и не меняют по ходу. */
function Conditions({ view }: { view: ContestView }) {
  const t = useT();
  const { contest: c } = view;
  const rows: [string, string][] = [
    [t('Что присылать'), t(KIND_NAMES[c.kind] ?? c.kind)],
    [
      t('Кто голосует'),
      c.vote_mode === 'public'
        ? t('все, кто вошёл')
        : c.vote_mode === 'jury'
          ? t('жюри платформы')
          : t('все и жюри (голос жюри весит больше)'),
    ],
    [t('Голосов на человека'), String(c.votes_per_user)],
    [t('Мест'), String(c.winners)],
  ];
  if (c.prize) rows.push([t('Приз'), c.prize]);
  if (c.submit_to) rows.push([t('Приём работ до'), fmtDate(c.submit_to)]);
  if (c.vote_to) rows.push([t('Голосование до'), fmtDate(c.vote_to)]);
  if (c.result_hash) rows.push([t('Снимок итогов'), `${c.result_hash.slice(0, 16)}…`]);

  return (
    <div style={{ marginTop: 14 }}>
      <div className="kcond">
        {rows.map(([k, v]) => (
          <div key={k} className="krow">
            <span className="k">{k}</span>
            <span className="v">{v}</span>
          </div>
        ))}
      </div>
      <p className="faint" style={{ fontSize: 11.5, marginTop: 10 }}>
        {view.tally_hidden
          ? t('Голоса скрыты до конца голосования: видимый счётчик тянет голоса к лидеру.')
          : t('Один человек — один голос за работу. Итоги фиксируются снимком, который нельзя переписать незаметно.')}
      </p>
    </div>
  );
}

/** Приём работы: форма видна только на этапе приёма. */
function SubmitBlock({ view, reload }: { view: ContestView; reload: () => void }) {
  const t = useT();
  const { contest: c, mine } = view;
  const { signedIn } = useSession();
  const toast = useToast();
  const [title, setTitle] = useState(mine?.title ?? '');
  const [body, setBody] = useState(mine?.body ?? '');
  const [team, setTeam] = useState(mine?.team ?? '');
  const [media, setMedia] = useState(mine?.media_url ?? '');
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const needsMedia = c.kind === 'photo' || c.kind === 'illustration' || c.kind === 'video';

  if (!signedIn) {
    return (
      <Card style={{ marginBottom: 18 }}>
        <div className="row" style={{ gap: 10 }}>
          <span className="sub">{t('Чтобы прислать работу и голосовать, нужно войти.')}</span>
          <span className="spacer" />
          <Link to="/login" className="btn pri">
            {t('Войти')}
          </Link>
        </div>
      </Card>
    );
  }

  const upload = async (f: File | undefined) => {
    if (!f) return;
    setBusy(true);
    try {
      setMedia(await api.uploadMedia(f, 'me'));
    } catch (err) {
      toast(err instanceof Error ? err.message : t('Файл не загрузился'));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.submitEntry(c.id, { title, body, team, media_url: media });
      toast(mine ? t('Работа обновлена — она снова уйдёт на проверку') : t('Работа отправлена на проверку'));
      reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : t('Не получилось отправить'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ marginBottom: 18 }}>
      <div className="sechead" style={{ marginBottom: 12 }}>
        <span className="t">{mine ? t('Твоя работа') : t('Прислать работу')}</span>
        <span className="n">{t('одна работа на человека, до публикации её смотрит модератор')}</span>
      </div>

      <Field label={t('Название')}>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('Коротко')} />
      </Field>

      <Field label={c.kind === 'text' ? t('Текст') : t('Описание')}>
        <textarea
          value={body}
          rows={c.kind === 'text' ? 8 : 3}
          onChange={(e) => setBody(e.target.value)}
          placeholder={c.kind === 'text' ? t('Твой текст') : t('Что это и как сделано')}
        />
      </Field>

      {c.kind === 'project' && (
        <Field label={t('Команда')} hint={t('кто делал — можно одному')}>
          <input value={team} onChange={(e) => setTeam(e.target.value)} placeholder={t('Название команды')} />
        </Field>
      )}

      <Field
        label={needsMedia ? t('Файл работы') : t('Файл или ссылка')}
        hint={t('JPEG, PNG, WebP, MP4 или WebM')}
      >
        <div className="row" style={{ gap: 8 }}>
          <input
            value={media}
            onChange={(e) => setMedia(e.target.value)}
            placeholder={t('https://… или загрузи файл')}
          />
          <Button size="sm" icon="upload" disabled={busy} onClick={() => file.current?.click()}>
            {t('Файл')}
          </Button>
          <input
            ref={file}
            type="file"
            accept="image/*,video/mp4,video/webm"
            hidden
            onChange={(e) => void upload(e.target.files?.[0])}
          />
        </div>
      </Field>

      {media && <EntryMedia url={media} kind={c.kind} />}

      <div className="row" style={{ marginTop: 12 }}>
        <span className="spacer" />
        <Button
          variant="pri"
          disabled={busy || (needsMedia && !media) || (!needsMedia && !body.trim() && !media)}
          onClick={() => void submit()}
        >
          {mine ? t('Обновить работу') : t('Отправить')}
        </Button>
      </div>
    </Card>
  );
}

/**
 * Файл работы.
 *
 * Если картинка не открылась, на её месте стоит заглушка со ссылкой, а не
 * сломанная иконка браузера: ссылку в работе может оставить и сам участник, и
 * внешний адрес однажды перестаёт отвечать.
 */
function EntryMedia({ url, kind }: { url: string; kind: string }) {
  const t = useT();
  const video = kind === 'video' || /\.(mp4|webm)$/i.test(url);
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <a className="kmedia dead" href={url} target="_blank" rel="noreferrer">
        <Icon name="external" size={16} />
        <span>{t('файл не открывается — посмотреть по ссылке')}</span>
      </a>
    );
  }

  return (
    <div className="kmedia">
      {video ? (
        <video src={url} controls preload="metadata" onError={() => setFailed(true)} />
      ) : (
        <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} />
      )}
    </div>
  );
}

function EntryCard({
  entry: e,
  view,
  reload,
  place,
}: {
  entry: ContestEntry;
  view: ContestView;
  reload: () => void;
  place?: number;
}) {
  const plural = usePlural();
  const t = useT();
  const { contest: c } = view;
  const { signedIn } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const voting = c.status === 'voting';
  const own = view.mine?.id === e.id;
  // e.mine — «я уже голосовал за эту работу», а не «это моя работа».
  const canVote = voting && signedIn && !own && !e.mine && view.votes_left > 0;

  const vote = async () => {
    setBusy(true);
    try {
      await api.vote(c.id, e.id);
      toast('Голос учтён');
      reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : t('Голос не прошёл'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="kentry">
      {place && <span className="kplace mono">{place}</span>}
      {e.media_url && <EntryMedia url={e.media_url} kind={c.kind} />}
      <div style={{ padding: e.media_url ? '10px 0 0' : 0 }}>
        <div className="row" style={{ gap: 8 }}>
          <strong style={{ fontSize: 14 }}>{e.title || t('Без названия')}</strong>
          <span className="spacer" />
          {!view.tally_hidden && (
            <span className="mono faint" style={{ fontSize: 12 }}>
              {num(e.votes)} {plural(e.votes, 'голос', 'голоса', 'голосов')}
            </span>
          )}
        </div>
        {e.body && (
          <p className="sub" style={{ marginTop: 6, whiteSpace: 'pre-line' }}>
            {e.body}
          </p>
        )}
        <div className="row faint" style={{ gap: 8, fontSize: 11.5, marginTop: 8 }}>
          <span>{e.team || e.author || t('участник')}</span>
          <span className="spacer" />
          {own && <Badge>{t('твоя работа')}</Badge>}
          {voting && !own && (
            e.mine ? (
              <Badge tone="good" icon="check">
                {t('твой голос')}
              </Badge>
            ) : (
              <Button
                size="sm"
                variant={canVote ? 'pri' : 'out'}
                icon="star"
                disabled={!canVote || busy}
                onClick={() => void vote()}
              >
                {!signedIn
                  ? t('Войти, чтобы голосовать')
                  : view.votes_left > 0
                    ? t('Голосовать')
                    : t('Голоса кончились')}
              </Button>
            )
          )}
        </div>
      </div>
    </Card>
  );
}
